/**
 * ViewProfile form body sections (S3776 complexity extraction).
 */
import React from 'react';
function getJoinDateSource(database: unknown): unknown {
  if (typeof database !== 'object' || database === null) {
    return undefined;
  }
  const record = database as {
    created_at?: unknown;
    createdAt?: unknown;
    date_of_activation?: unknown;
  };
  return record.created_at ?? record.createdAt ?? record.date_of_activation;
}

export function ViewProfileHeaderActions(
  props: Readonly<{
    isEditing: boolean;
    isSubmitting: boolean;
    isValid: boolean;
    dirty: boolean;
    resetForm: () => void;
    clearPasswordFields: () => void;
    setIsEditing: (v: boolean) => void;
  }>
) {
  const { isEditing, isSubmitting, isValid, dirty, resetForm, clearPasswordFields, setIsEditing } =
    props;

  const saveButtonTitle = (() => {
    if (!dirty) {
      return 'No changes to save';
    }
    if (!isValid) {
      return 'Full name and a valid 10-digit mobile number are required';
    }
    return undefined;
  })();

  if (isEditing) {
    return (
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={() => {
            resetForm();
            clearPasswordFields();
            setIsEditing(false);
          }}
          disabled={isSubmitting}
          className="h-9 rounded-[5.2px] border border-[#e5e5e5] bg-white px-4 text-[14px] text-[#0a0a0a] hover:bg-[#f3f3f5]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!isValid || isSubmitting || !dirty}
          title={saveButtonTitle}
          className="h-9 rounded-[5.2px] border-0 bg-[#0f91b2] px-4 text-[14px] text-[#eafdfe] hover:bg-[#0d7d9a] disabled:opacity-60"
        >
          {isSubmitting ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        onClick={() => setIsEditing(true)}
        className="h-9 shrink-0 rounded-[5.2px] border-0 bg-[#0f91b2] px-4 text-[14px] text-[#eafdfe] hover:bg-[#0d7d9a]"
      >
        Edit Details
      </button>
    </div>
  );
}

export function ViewProfileSecuritySection(
  props: Readonly<{
    isEditing: boolean;
    pwCurrent: string;
    setPwCurrent: (v: string) => void;
    pwNew: string;
    setPwNew: (v: string) => void;
    pwConfirm: string;
    setPwConfirm: (v: string) => void;
    pwErrors: any;
    setPwErrors: (v: any) => void;
    isPwSubmitting: boolean;
    handleUpdatePassword: () => void | Promise<void>;
  }>
) {
  const {
    isEditing,
    pwCurrent,
    setPwCurrent,
    pwNew,
    setPwNew,
    pwConfirm,
    setPwConfirm,
    pwErrors,
    setPwErrors,
    isPwSubmitting,
    handleUpdatePassword,
  } = props;

  return (
    <section className="view-profile-card view-profile-card--section">
      <div className="view-profile-card-section-head">
        <h3 className="view-profile-card-section-title">Security</h3>
        <hr className="view-profile-card-section-rule" />
      </div>
      <div className="view-profile-card-section-body">
        <div className="view-profile-security-fields">
          <div className="view-profile-field">
            <label
              className="view-profile-field-label"
              htmlFor="view-profile-current-password"
            >
              Current Password
            </label>
            <input
              id="view-profile-current-password"
              className="view-profile-password-input"
              type="password"
              value={pwCurrent}
              disabled={!isEditing}
              autoComplete="current-password"
              onChange={e => {
                setPwCurrent(e.target.value);
                setPwErrors(null);
              }}
            />
            {isEditing && pwErrors?.current ? (
              <span className="view-profile-field-error">{pwErrors.current}</span>
            ) : null}
          </div>
          <div className="view-profile-field">
            <label
              className="view-profile-field-label"
              htmlFor="view-profile-new-password"
            >
              New Password
            </label>
            <input
              id="view-profile-new-password"
              className="view-profile-password-input"
              type="password"
              value={pwNew}
              disabled={!isEditing}
              autoComplete="new-password"
              onChange={e => {
                setPwNew(e.target.value);
                setPwErrors(null);
              }}
            />
            {isEditing && pwErrors?.new ? (
              <span className="view-profile-field-error">{pwErrors.new}</span>
            ) : null}
          </div>
          <div className="view-profile-field">
            <label
              className="view-profile-field-label"
              htmlFor="view-profile-confirm-password"
            >
              Confirm New Password
            </label>
            <input
              id="view-profile-confirm-password"
              className="view-profile-password-input"
              type="password"
              value={pwConfirm}
              disabled={!isEditing}
              autoComplete="new-password"
              onChange={e => {
                setPwConfirm(e.target.value);
                setPwErrors(null);
              }}
            />
            {isEditing && pwErrors?.confirm ? (
              <span className="view-profile-field-error">{pwErrors.confirm}</span>
            ) : null}
          </div>
        </div>
        {isEditing ? (
          <div className="view-profile-password-actions">
            <button
              type="button"
              className="view-profile-password-submit view-profile-password-submit--secondary mt-2"
              disabled={isPwSubmitting}
              onClick={() => {
                Promise.resolve(handleUpdatePassword()).catch(() => {
                  /* ignore */
                });
              }}
            >
              {isPwSubmitting ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function ViewProfilePersonalDetailsSection(
  props: Readonly<{
    values: any;
    userData: any;
    isEditing: boolean;
    handleChange: any;
    setFieldValue: any;
    getFieldError: (name: string) => string | undefined;
    formatPhoneDisplay: (phone: unknown) => string;
    formatDepartmentDisplay: (department: unknown) => string;
    formatJoinDate: (source: unknown) => string;
    getFacilityName: () => string;
  }>
) {
  const {
    values,
    userData,
    isEditing,
    handleChange,
    setFieldValue,
    getFieldError,
    formatPhoneDisplay,
    formatDepartmentDisplay,
    formatJoinDate,
    getFacilityName,
  } = props;

  return (
    <section className="view-profile-card view-profile-card--section">
      <div className="view-profile-card-section-head">
        <h3 className="view-profile-card-section-title">Personal Details</h3>
        <hr className="view-profile-card-section-rule" />
      </div>
      <div className="view-profile-card-section-body">
        <div className="view-profile-field-grid">
          <div className="view-profile-field">
            <div className="view-profile-field-label-row">
              <span className="view-profile-field-label">Employee ID</span>
            </div>
            <div className="view-profile-readonly-box">
              {String(
                values.employeeId ||
                  userData?.fromDatabase?.employee_id ||
                  userData?.fromDatabase?.doctor_id ||
                  ''
              ).trim() || '—'}
            </div>
          </div>

          <div className="view-profile-field">
            <div className="view-profile-field-label-row">
              <span className="view-profile-field-label">Full Name</span>
              <span className="view-profile-required">*</span>
            </div>
            {isEditing ? (
              <>
                <input
                  className="view-profile-editable-input"
                  name="fullName"
                  value={values.fullName}
                  onChange={handleChange}
                  autoComplete="name"
                  aria-invalid={Boolean(getFieldError('fullName'))}
                />
                {getFieldError('fullName') ? (
                  <span className="view-profile-field-error">{getFieldError('fullName')}</span>
                ) : null}
              </>
            ) : (
              <div className="view-profile-readonly-box">{String(values.fullName || '')}</div>
            )}
          </div>

          <div className="view-profile-field">
            <div className="view-profile-field-label-row">
              <span className="view-profile-field-label">Email Address</span>
            </div>
            <div className="view-profile-readonly-box">{String(values.email || '')}</div>
          </div>

          <div className="view-profile-field">
            <div className="view-profile-field-label-row">
              <span className="view-profile-field-label">Phone Number</span>
              <span className="view-profile-required">*</span>
            </div>
            {isEditing ? (
              <>
                <input
                  className="view-profile-editable-input"
                  name="phoneNo"
                  value={values.phoneNo}
                  onChange={e => {
                    const digits = e.target.value.replaceAll(/\D/g, '').slice(0, 10);
                    setFieldValue('phoneNo', digits).catch(() => undefined);
                  }}
                  inputMode="numeric"
                  maxLength={14}
                  autoComplete="tel"
                  aria-invalid={Boolean(getFieldError('phoneNo'))}
                  aria-label="Mobile number (10 digits)"
                />
                {getFieldError('phoneNo') ? (
                  <span className="view-profile-field-error">{getFieldError('phoneNo')}</span>
                ) : null}
              </>
            ) : (
              <div className="view-profile-readonly-box">{formatPhoneDisplay(values.phoneNo)}</div>
            )}
          </div>

          <div className="view-profile-field">
            <div className="view-profile-field-label-row">
              <span className="view-profile-field-label">Role</span>
            </div>
            <div className="view-profile-readonly-box">
              {String(userData?.fromDatabase?.role || '—')}
            </div>
          </div>

          <div className="view-profile-field">
            <div className="view-profile-field-label-row">
              <span className="view-profile-field-label">Facility</span>
            </div>
            <div className="view-profile-readonly-box">{getFacilityName()}</div>
          </div>

          <div className="view-profile-field">
            <div className="view-profile-field-label-row">
              <span className="view-profile-field-label">Department</span>
            </div>
            <div className="view-profile-readonly-box">
              {formatDepartmentDisplay(userData?.fromDatabase?.department)}
            </div>
          </div>

          <div className="view-profile-field">
            <div className="view-profile-field-label-row">
              <span className="view-profile-field-label">Join Date</span>
            </div>
            <div className="view-profile-readonly-box">
              {formatJoinDate(getJoinDateSource(userData?.fromDatabase))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
